/**
 * @lodex-pro/web-portal/hooks/useOperatorApiKeys — the operator admin's API keys (operator
 * portal rework PR 9, migration 0213). OPERATOR_ADMIN only on the API
 * (MANAGE_OPERATOR_API_KEYS); foremen and pumpers get 403, so the page never mounts these
 * hooks for them.
 *
 *   GET  /v1/operator-portal/api-keys              the keys, the live count, the plan's limit
 *   GET  /v1/operator-portal/api-keys/haulers      the haulers a key can be issued for
 *   POST /v1/operator-portal/api-keys              issue; the secret comes back once
 *   POST /v1/operator-portal/api-keys/:id/rotate   a successor; the old key keeps an overlap
 *   POST /v1/operator-portal/api-keys/:id/revoke   immediately
 *
 * Every write invalidates the list (and the usage page, whose credential set it changes).
 * Response types are `import type` only (DEC-149 §9).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  IssueOperatorApiKeyRequest,
  IssuedOperatorApiKey,
  OperatorApiKeyHauler,
  OperatorApiKeyView,
  OperatorApiKeysResponse,
} from '@lodex-pro/shared/validation/operator-api-credentials';

export const PORTAL_API_KEYS_KEY: readonly string[] = ['operator-portal', 'api-keys'];
export const PORTAL_API_KEY_HAULERS_KEY: readonly string[] = [
  'operator-portal',
  'api-keys',
  'haulers',
];

export function usePortalApiKeys(): ReturnType<typeof useApiQuery<OperatorApiKeysResponse>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorApiKeysResponse>(opts, '/v1/operator-portal/api-keys', {
    queryKey: PORTAL_API_KEYS_KEY,
    staleTime: 10_000,
  });
}

export function usePortalApiKeyHaulers(): ReturnType<
  typeof useApiQuery<{ haulers: OperatorApiKeyHauler[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ haulers: OperatorApiKeyHauler[] }>(
    opts,
    '/v1/operator-portal/api-keys/haulers',
    { queryKey: PORTAL_API_KEY_HAULERS_KEY, staleTime: 60_000 },
  );
}

function useInvalidateKeys(): () => void {
  const qc = useQueryClient();
  return (): void => {
    void qc.invalidateQueries({ queryKey: PORTAL_API_KEYS_KEY });
    void qc.invalidateQueries({ queryKey: ['operator-portal', 'api-usage'] });
  };
}

export function useIssuePortalApiKey(): ReturnType<
  typeof useApiMutation<IssueOperatorApiKeyRequest, IssuedOperatorApiKey>
> {
  const opts = useApiOpts();
  const invalidate = useInvalidateKeys();
  return useApiMutation<IssueOperatorApiKeyRequest, IssuedOperatorApiKey>(
    opts,
    '/v1/operator-portal/api-keys',
    'POST',
    { onSettled: invalidate },
  );
}

export interface RotateBody {
  readonly overlapHours: number;
}

export function useRotatePortalApiKey(
  id: string,
): ReturnType<typeof useApiMutation<RotateBody, IssuedOperatorApiKey>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateKeys();
  return useApiMutation<RotateBody, IssuedOperatorApiKey>(
    opts,
    `/v1/operator-portal/api-keys/${id}/rotate`,
    'POST',
    { onSettled: invalidate },
  );
}

export interface RevokeBody {
  readonly reason: string;
}

export function useRevokePortalApiKey(
  id: string,
): ReturnType<typeof useApiMutation<RevokeBody, { key: OperatorApiKeyView }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateKeys();
  return useApiMutation<RevokeBody, { key: OperatorApiKeyView }>(
    opts,
    `/v1/operator-portal/api-keys/${id}/revoke`,
    'POST',
    { onSettled: invalidate },
  );
}
